import React from 'react';
import { DashboardLayout } from '../../client/components/Sidebar';
import DefaultAdminLayout from '../../admin/layout/DefaultLayout';
import { type AuthUser } from 'wasp/auth';

export interface IndividualEditPageLayoutProps {
  children: React.ReactNode;
  sidebarContent?: React.ReactNode;
  customTopBarContent?: React.ReactNode;
  layoutVariant?: 'dashboard' | 'admin' | 'taxonomy';
  authUser?: AuthUser;
}

/**
 * Reusable layout component for individual item edit pages.
 * Provides consistent structure with form content area and right sidebar.
 * 
 * @example
 * ```tsx
 * <IndividualEditPageLayout
 *   sidebarContent={
 *     <>
 *       <ActionButtons />
 *       <SettingsSection />
 *     </>
 *   }
 * >
 *   <FormContent />
 * </IndividualEditPageLayout>
 * ```
 */
export default function IndividualEditPageLayout({
  children,
  sidebarContent,
  customTopBarContent,
  layoutVariant = 'dashboard',
  authUser
}: IndividualEditPageLayoutProps) {
  const isNestedLayout = layoutVariant === 'admin' || layoutVariant === 'taxonomy';

  const pageContent = (
    <div className={`${isNestedLayout ? 'h-full' : 'min-h-screen'} bg-background`}>
      <div className="flex h-full w-full items-start">
        {/* Main Content Area */}
        <div className="flex-1">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className={`flex-1 ${isNestedLayout ? 'pl-6 pt-4 pb-12 pr-6' : 'px-6 py-4 md:px-8 md:py-6 2xl:px-10 2xl:py-8'}`}>
              {children}
            </div>

            {/* Right Sidebar - flush to the edge */}
            {sidebarContent && (
              <div className={`hidden lg:flex w-80 border-l border-border flex-col sticky top-0 self-start bg-card ${isNestedLayout ? 'h-[calc(100vh-var(--header-height,64px))] overflow-y-auto' : 'min-h-screen'}`}>
                {sidebarContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (layoutVariant === 'admin' && authUser) {
    return <DefaultAdminLayout user={authUser}>{pageContent}</DefaultAdminLayout>;
  }

  // 'taxonomy' — App.tsx already provides TaxonomyLayout, just render content
  if (layoutVariant === 'taxonomy') {
    return pageContent;
  }

  return <DashboardLayout customTopBarContent={customTopBarContent}>{pageContent}</DashboardLayout>;
}




